/* results-layout.js - the click half of the "View as" grid and list toggle. It
   names no route: a switcher is any element carrying data-results-layout, and
   that attribute is the whole contract between a toolbar and this file. The
   collection arms from here today and the search toolbar will need nothing
   added below.

   IT DOES NOT RESTORE THE STORED PREFERENCE ON A COLD LOAD. The section carries
   a small inline script for that, placed between the toolbar and the grid, so
   the first paint is already correct. Deferring the restore to this file would
   paint a grid and then snap it into a list, which is a visible flash and a
   layout shift charged against a measured CLS budget.

   THE STATE GOES ON THE `.results` ROOT AND NOWHERE INSIDE IT. assets/facets.js
   repaints the grid's region by assigning innerHTML after every filter and sort
   change, so an attribute written on or under the grid is destroyed silently on
   the next change. The root is never replaced, only its regions, so one write
   there survives every repaint.

   NOTHING IS FETCHED AND THE URL IS NOT TOUCHED. This is a display preference,
   not a filter: it belongs in this browser and not in a shareable address, and
   facets.js owns the history entries for this page.

   TEARDOWN IS KEYED BY THE SWITCHER'S DOM ID, never by the node. The editor
   replaces a section's innerHTML and fires shopify:section:load with NO matching
   unload, so the node we armed is already detached when we hear about it. A
   sweep drops any entry whose switcher has left the document. */
(function () {
  'use strict';

  var flag = document.documentElement;
  if (flag.hasAttribute('data-pp-results-layout')) return;
  flag.setAttribute('data-pp-results-layout', '');

  var SWITCHER_SELECTOR = '[data-results-layout]';
  var BUTTON_SELECTOR = '[data-layout-mode]';
  var STORE_KEY = 'pp:results-layout';
  var instances = Object.create(null);

  function isMode(value) {
    return value === 'grid' || value === 'list';
  }

  /* Every access is wrapped: localStorage THROWS rather than returning null
     when a browser refuses it (private mode, a full quota, a policy). A
     preference that cannot be stored costs the memory, never the toggle. */
  function readStored() {
    var value = null;
    try {
      value = window.localStorage.getItem(STORE_KEY);
    } catch (error) {
      return null;
    }
    return isMode(value) ? value : null;
  }

  function writeStored(mode) {
    try {
      window.localStorage.setItem(STORE_KEY, mode);
    } catch (error) {
      return;
    }
  }

  /* The snippet always stamps an id, and a page carries one toolbar, so the
     fallback is a floor rather than a case that happens. */
  function keyOf(switcher) {
    return switcher.id || 'results-layout';
  }

  /* `.results` is the shared root class both hosts wear. data-facets-root is the
     same element on every route that has filters, and is the floor for a host
     that ever drops the class. */
  function rootOf(switcher) {
    return switcher.closest('.results') || switcher.closest('[data-facets-root]');
  }

  function apply(switcher, mode) {
    var root = rootOf(switcher);
    if (root) root.setAttribute('data-layout', mode);

    Array.prototype.forEach.call(switcher.querySelectorAll(BUTTON_SELECTOR), function (button) {
      button.setAttribute('aria-pressed', button.getAttribute('data-layout-mode') === mode ? 'true' : 'false');
    });
  }

  function release(key) {
    var inst = instances[key];
    if (!inst) return;
    inst.controller.abort();
    delete instances[key];
  }

  function sweep() {
    Object.keys(instances).forEach(function (key) {
      if (!instances[key].switcher.isConnected) release(key);
    });
  }

  function arm(switcher) {
    var key = keyOf(switcher);

    /* Same id, new node, or simply the same node reached twice: whatever still
       listens goes before we register again. */
    release(key);

    var controller = new AbortController();
    instances[key] = { switcher: switcher, controller: controller };

    switcher.addEventListener(
      'click',
      function (event) {
        var node = event.target;
        if (!node || typeof node.closest !== 'function') return;

        var button = node.closest(BUTTON_SELECTOR);
        if (!button || !switcher.contains(button)) return;

        var mode = button.getAttribute('data-layout-mode');
        if (!isMode(mode)) return;

        apply(switcher, mode);
        writeStored(mode);
      },
      { signal: controller.signal }
    );

    /* The editor rebuilds a section by assigning innerHTML, which does NOT run
       the inline restore that shipped with it, so the stored preference is
       re-applied here. Idempotent on a normal load, where the inline script has
       already written the same value. */
    var stored = readStored();
    if (stored) apply(switcher, stored);
  }

  function init(scope) {
    sweep();
    var switchers = (scope || document).querySelectorAll(SWITCHER_SELECTOR);
    Array.prototype.forEach.call(switchers, arm);
  }

  init();

  document.addEventListener('shopify:section:load', function (event) {
    var scope = event.target && event.target.querySelectorAll ? event.target : document;
    init(scope);
  });

  document.addEventListener('shopify:section:unload', function () {
    sweep();
  });
})();
